import { useState } from 'react';

import { ApolloQueryResult } from '@apollo/client/core/types';
import { Money, formatDate, formatMoney, parseMoney } from '@betterment/core';
import { useGraphQLMutation } from '@betterment/graphql';
import { Button, FormikField as Field, Input, PancakeStack, Select, Sheet, VStack } from '@betterment/samba-react';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { TaxFilingStatus } from '../../../../../graphql/schema';
import { convertDisplayableErrors } from '../../../../../Shared/displayable-errors';
import { formatTaxFilingStatus } from '../../../../../Shared/graphql/utils';
import { UpdateUserFinancialProfileDocument } from '../../graphql/mutate.financialProfile.generated';
import { RetirementPurposeCreationOptionQuery } from '../../graphql/query.retirementPurposeCreationOption.generated';

const MARRIED_STATUSES: TaxFilingStatus[] = [
  TaxFilingStatus.MarriedFilingJointly,
  TaxFilingStatus.MarriedFilingSeparately,
  TaxFilingStatus.MarriedFilingSeparatelyLivingApart,
];

const schema = Yup.object({
  annualPretaxIncome: Yup.string().required('Current income is required'),
  taxFilingStatus: Yup.string()
    .oneOf(Object.values(TaxFilingStatus), 'Please select a tax filing status')
    .required('Tax filing status is required'),
  spouseName: Yup.string().when('taxFilingStatus', {
    is: (val: TaxFilingStatus) => MARRIED_STATUSES.includes(val),
    then: s => s.required('Spouse name is required'),
  }),
  spouseDateOfBirth: Yup.string().when('taxFilingStatus', {
    is: (val: TaxFilingStatus) => MARRIED_STATUSES.includes(val),
    then: s => s.required('Spouse date of birth is required'),
  }),
  spouseEmploymentStatus: Yup.string().when('taxFilingStatus', {
    is: (val: TaxFilingStatus) => MARRIED_STATUSES.includes(val),
    then: s => s.oneOf(['full_time', 'retired']).required('Spouse employment status is required'),
  }),
  spouseIncome: Yup.string().when('taxFilingStatus', {
    is: (val: TaxFilingStatus) => MARRIED_STATUSES.includes(val),
    then: s => s.required("Spouse's income is required"),
  }),
});

type FormValues = Yup.InferType<typeof schema>;

const SpouseFields: React.FC = () => {
  const { values } = useFormikContext<FormValues>();

  if (!MARRIED_STATUSES.includes(values.taxFilingStatus)) return null;

  return (
    <>
      <Field name="spouseName">
        <Field.Label>Spouse name</Field.Label>
        <Field.Control>
          <Input type="text" />
        </Field.Control>
        <Field.ErrorMessage />
      </Field>
      <Field name="spouseDateOfBirth">
        <Field.Label>Spouse date of birth</Field.Label>
        <Field.Control>
          <Input mask="date-mm/dd/yyyy" />
        </Field.Control>
        <Field.ErrorMessage />
      </Field>
      <Field name="spouseEmploymentStatus">
        <Field.Label>Spouse's employment status</Field.Label>
        <Field.Control>
          <Select>
            <Select.Trigger appearance="default" aria-label="Select spouse's employment status">
              <Select.Value placeholder="Select employment status" />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="full_time">Not retired</Select.Item>
              <Select.Item value="retired">Retired</Select.Item>
            </Select.Content>
          </Select>
        </Field.Control>
        <Field.ErrorMessage />
      </Field>
      <Field name="spouseIncome">
        <Field.Label>Spouse's income</Field.Label>
        <Field.Control>
          <Input mask="number" leadingSymbol="$" />
        </Field.Control>
        <Field.ErrorMessage />
      </Field>
    </>
  );
};

type Spouse = {
  name?: string | null;
  dateOfBirth?: string | null;
  annualPretaxIncome?: Money | null;
};

type Props = {
  annualPretaxIncome?: Money;
  taxFilingStatus?: TaxFilingStatus;
  spouse?: Spouse | null;
  updateData: () => Promise<ApolloQueryResult<RetirementPurposeCreationOptionQuery>>;
};

export const ReviewProfileSheet: React.FC<Props> = ({
  annualPretaxIncome = Money(0),
  taxFilingStatus = TaxFilingStatus.Single,
  spouse,
  updateData,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [updateFinancialProfile] = useGraphQLMutation(UpdateUserFinancialProfileDocument);

  const initialValues: FormValues = {
    annualPretaxIncome: formatMoney(annualPretaxIncome, 'raw'),
    taxFilingStatus,
    spouseName: spouse?.name ?? '',
    spouseDateOfBirth: spouse?.dateOfBirth ? formatDate(spouse.dateOfBirth, 'longOrdinalSlashed') : '',
    spouseEmploymentStatus: 'full_time',
    spouseIncome: formatMoney(spouse?.annualPretaxIncome ?? Money(0), 'raw'),
  };

  const handleSubmit = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    const userProfileUpdateResult = await updateFinancialProfile({
      variables: {
        input: {
          annualPretaxIncome: parseMoney(values.annualPretaxIncome),
          taxFilingStatus: values.taxFilingStatus,
          ...(values.spouseName &&
            values.spouseDateOfBirth &&
            values.spouseEmploymentStatus &&
            values.spouseIncome && {
              spouse: {
                name: values.spouseName,
                dateOfBirth: formatDate(new Date(values.spouseDateOfBirth), 'iso8601'),
                employmentStatus: values.spouseEmploymentStatus,
                annualPretaxIncome: parseMoney(values.spouseIncome)!,
              },
            }),
        },
      },
    });

    if (userProfileUpdateResult.data?.updateUserProfile?.result) {
      await updateData();
      setIsOpen(false);
    }

    const displayableErrors = userProfileUpdateResult.data?.updateUserProfile?.displayableErrors;
    if (displayableErrors && displayableErrors.length > 0) {
      formikHelpers.setErrors(convertDisplayableErrors(displayableErrors));
    }
  };

  return (
    <Sheet overlay="weak" open={isOpen} onOpenChange={setIsOpen}>
      <Sheet.Trigger asChild={true}>
        <Button type="button" variant="textPrimary" css={{ fontSize: 'fontSize.50' }}>
          Review profile
        </Button>
      </Sheet.Trigger>
      <Sheet.Content>
        <PancakeStack css={{ height: '100vh', overflowY: 'auto' }}>
          <PancakeStack.Header css={{ p: 'space.300', pt: 'space.500' }}>
            <Sheet.Title>Review financial profile</Sheet.Title>
          </PancakeStack.Header>
          <PancakeStack.Main css={{ px: 'space.300', pb: 'space.300', flex: 1 }}>
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={schema}
              validateOnChange={false}
            >
              {({ isSubmitting }) => (
                <Form>
                  <VStack css={{ gap: 'space.300' }}>
                    <Field name="annualPretaxIncome">
                      <Field.Label>Current income</Field.Label>
                      <Field.Control>
                        <Input mask="number" leadingSymbol="$" />
                      </Field.Control>
                      <Field.ErrorMessage />
                    </Field>
                    <Field name="taxFilingStatus">
                      <Field.Label>Tax status</Field.Label>
                      <Field.Control>
                        <Select>
                          <Select.Trigger appearance="default" aria-label="Select tax status">
                            <Select.Value placeholder="Select tax status" />
                          </Select.Trigger>
                          <Select.Content>
                            {Object.values(TaxFilingStatus).map(status => (
                              <Select.Item key={status} value={status}>
                                {formatTaxFilingStatus(status)}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select>
                      </Field.Control>
                      <Field.ErrorMessage />
                    </Field>
                    <SpouseFields />
                    <Button type="submit" loading={isSubmitting} css={{ width: 'fit-content' }}>
                      Save changes
                    </Button>
                  </VStack>
                </Form>
              )}
            </Formik>
          </PancakeStack.Main>
        </PancakeStack>
      </Sheet.Content>
    </Sheet>
  );
};
